import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Briefcase, UserCheck, MapPin, ShieldCheck } from "lucide-react";

const MODULE_LABELS = {
  dashboard: "Dashboard", catalog: "Product Catalogue", inventory: "Inventory", orders: "Incoming Orders", procurement: "Purchase Orders",
  partners: "Business Partners", sales: "Sales & Revenue", finance: "Expenses & Finance", logistics: "Logistics & Delivery",
  reports: "Reports & Analytics", org_settings: "Organisation Settings",
};

function groupPerms(perms) {
  const g = {};
  (perms || []).forEach((p) => { const [m, a] = p.split("."); (g[m] = g[m] || []).push(a); });
  return Object.entries(g);
}

export function EmployeeWelcome() {
  const { user, business, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.role !== "operator" || user.welcome_seen) return null;
  const done = async () => { setBusy(true); try { await api.post("/me/welcome-seen"); await refresh(); } finally { setBusy(false); } };
  const groups = groupPerms(user.permissions);
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto [&>button]:hidden" data-testid="employee-welcome-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Welcome to {business?.name}, {user.name?.split(" ")[0]}!</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">Here's how your account is set up. Your Super Admin can change this anytime.</p>
        <div className="space-y-3">
          <div className="flex gap-3 rounded-xl bg-[#FAF8F5] p-3" data-testid="welcome-roles">
            <Briefcase className="mt-0.5 h-5 w-5 text-[#0D5C3A]" />
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-400">Your role{(user.assignments || []).length > 1 ? "s" : ""}</p>
              {(user.assignments || []).map((a) => <p key={a.department + a.role} className="text-sm font-semibold text-stone-800">{a.role} · <span className="font-normal text-stone-500">{a.department_name}</span></p>)}
              {!(user.assignments || []).length && <p className="text-sm text-stone-500">Not assigned yet</p>}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex gap-3 rounded-xl bg-[#FAF8F5] p-3" data-testid="welcome-manager"><UserCheck className="mt-0.5 h-5 w-5 text-[#0D5C3A]" /><div><p className="text-xs uppercase tracking-wider text-stone-400">Reports to</p><p className="text-sm font-semibold text-stone-800">{user.reporting_manager_name || "—"}</p></div></div>
            <div className="flex gap-3 rounded-xl bg-[#FAF8F5] p-3" data-testid="welcome-branch"><MapPin className="mt-0.5 h-5 w-5 text-[#0D5C3A]" /><div><p className="text-xs uppercase tracking-wider text-stone-400">Location</p><p className="text-sm font-semibold text-stone-800">{user.branch_name || "Head office"}</p></div></div>
          </div>
          <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="welcome-access">
            <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-stone-400"><ShieldCheck className="h-4 w-4 text-[#0D5C3A]" /> What you can access</p>
            {groups.length === 0 ? <p className="text-sm text-stone-500">No access yet — ask your Super Admin.</p> : (
              <div className="flex flex-wrap gap-2">
                {groups.map(([m, acts]) => <span key={m} className="rounded-full bg-[#F0ECE1] px-3 py-1 text-xs font-semibold text-[#0D5C3A]">{MODULE_LABELS[m] || m} <span className="font-normal capitalize text-stone-500">({acts.join(", ")})</span></span>)}
              </div>
            )}
          </div>
        </div>
        <div>
          <Button onClick={done} disabled={busy} data-testid="employee-welcome-continue-btn" className="w-full bg-[#0D5C3A] hover:bg-[#084229]">Get started</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
